import React, { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import styles from './Footer.module.css';

function Footer() {
  const [isExpanded, setIsExpanded] = useState(true);
  const [isTickerPaused, setIsTickerPaused] = useState(false);

  const footerGroups = useMemo(
    () => [
      {
        id: 'company',
        title: 'COMPANY',
        links: [
          { type: 'anchor', href: '/#home', label: 'Home', selector: '#home' },
          { type: 'anchor', href: '/#about', label: 'About Us', selector: '#about' },
          { type: 'anchor', href: '/#services', label: 'Product & Services', selector: '#services' },
          { type: 'route', to: '/contact-us', label: 'Contact Us' },
          {
            type: 'external',
            href: 'https://enit.nseindia.com/MemDirWeb/brokerDetailPage_Beta?memID=3184&h_MemType=members&memName=OUR%20BROKING%20SERVICES%20LLP',
            label: 'Know Your Broker (NSE)'
          }
        ]
      },
      {
        id: 'quick-links',
        title: 'QUICK LINKS',
        links: [
          { type: 'external', href: 'https://www.bseindia.com', label: 'BSE' },
          { type: 'external', href: 'https://www.nseindia.com', label: 'NSE' },
          { type: 'external', href: 'https://www.sebi.gov.in/', label: 'SEBI' },
          { type: 'external', href: 'https://scores.sebi.gov.in/', label: 'SCORES 2.0' },
          {
            type: 'external',
            href: 'https://investorhelpline.nseindia.com/ClientCollateral/welcomeCLUser',
            label: 'CLIENT COLLATERAL DETAILS - NSE'
          },
          { type: 'external', href: 'https://smartodr.in/login', label: 'SMART ODR' }
        ]
      },
      {
        id: 'investors',
        title: 'INVESTORS',
        links: [
          {
            type: 'external',
            href: 'https://evoting.cdslindia.com/Evoting/EvotingLogin',
            label: 'E-Voting'
          },
          { type: 'route', to: '/Faq', label: "FAQ's" },
          { type: 'route', to: '/InvestorHome', label: 'Investor Home' },
          { type: 'route', to: '/PrivacyPolicy', label: 'Privacy Policy' },
          { type: 'route', to: '/Tnc', label: 'Terms & Conditions' },
          { type: 'route', to: '/accessibility-statement', label: 'Accessibility Statement' }
        ]
      },
      {
        id: 'account',
        title: 'ACCOUNT',
        links: [{ type: 'route', to: '/FundTransfer', label: 'Fund Transfer' }]
      }
    ],
    []
  );

  const tickerItems = useMemo(
    () => [
      {
        title: '1. Stock Broking:',
        text: 'Prevent unauthorised transactions in your account → Update your mobile numbers/email IDs with your stock brokers. Receive information of your transactions directly from Exchange on your mobile/email at the end of the day... Issued in the interest of investors.'
      },
      {
        title: '2. Depository:',
        text: 'Prevent Unauthorized Transactions in your demat account → Update your Mobile Number with your Depository Participant. Receive alerts on your Registered Mobile for all debit and other important transactions in your demat account directly from CDSL/NSDL on the same day... Issued in the interest of investors.'
      },
      {
        title: '3. KYC:',
        text: 'KYC is a one time exercise while dealing in securities markets - once KYC is done through a SEBI registered intermediary (Broker, DP, Mutual Fund etc.), you need not undergo the same process again when you approach another intermediary.'
      },
      {
        title: '4. IPO:',
        text: 'If you are subscribing to an IPO, there is no need to issue a cheque. Please write the Bank account number and sign the IPO application form to authorize your bank to make payment in case of allotment. In case of non allotment the funds will remain in your bank account.'
      }
    ],
    []
  );

  const getStickyOffset = () => {
    const stickyNavbar = document.querySelector('.navbar.fixed-top');
    return stickyNavbar ? stickyNavbar.getBoundingClientRect().height + 16 : 16;
  };

  const focusTarget = (targetElement) => {
    if (!targetElement) {
      return;
    }

    if (!targetElement.hasAttribute('tabindex')) {
      targetElement.setAttribute('tabindex', '-1');
    }

    targetElement.focus({ preventScroll: true });
  };

  const scrollToSelector = (selector) => {
    const targetElement = document.querySelector(selector);

    if (!targetElement) {
      return false;
    }

    const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    const targetTop = window.scrollY + targetElement.getBoundingClientRect().top - getStickyOffset();

    window.scrollTo({
      top: Math.max(0, targetTop),
      behavior: prefersReducedMotion ? 'auto' : 'smooth'
    });

    window.setTimeout(() => {
      focusTarget(targetElement);
    }, prefersReducedMotion ? 0 : 260);

    return true;
  };

  const handleSectionLink = (event, selector) => {
    if (window.location.pathname !== '/') {
      return;
    }

    event.preventDefault();

    const didScroll = scrollToSelector(selector);

    if (didScroll) {
      window.history.replaceState(null, '', `/${selector}`);
    }
  };

  const renderFooterLink = (link) => {
    if (link.type === 'route') {
      return (
        <Link className={styles.footerLink} to={link.to}>
          {link.label}
        </Link>
      );
    }

    if (link.type === 'external') {
      return (
        <a href={link.href} target="_blank" rel="noopener noreferrer" className={styles.footerLink}>
          {link.label}
          <span className={styles.srOnly}> opens in a new tab</span>
        </a>
      );
    }

    return (
      <a
        href={link.href}
        className={styles.footerLink}
        onClick={(event) => handleSectionLink(event, link.selector)}
      >
        {link.label}
      </a>
    );
  };

  return (
    <footer className={styles.footerRoot} data-testid="footer-root">
      <div className="container">
        <div className={`row ${styles.footerTopRow}`}>
          <div className="col-lg-6 col-md-12">
            <div className={styles.footerBox}>
              <div className="row">
                <div className={`col-lg-4 ${styles.dividerColumn}`}>
                  <div className={styles.logoWrap}>
                    <img src="/img/logo.png" alt="Our Broking logo" />
                  </div>
                  <p className={styles.brandText}>
                    <span className={styles.companyName}>OUR BROKING</span>
                    <span className={styles.companySuffix}>Services LLP</span>
                  </p>
                </div>

                <div className={`col-md-4 ${styles.rightColumn} ${styles.dividerColumn}`}>
                  <address className={`${styles.addressBlock} ${styles.footerAddress}`}>
                    <p>
                      <strong>Registered Office (New):</strong>
                      <br />
                      1st Floor, House No. 40-G, BRS Nagar, Ludhiana, Punjab – 141012
                    </p>
                    <p>
                      <strong>Correspondence Address/ Old Registered Address(Operational):</strong>
                      <br />
                      Room no-561, LSE building, Firoze Gandhi Market, Ludhiana, Punjab-141001
                    </p>
                  </address>
                </div>

                <div className={`col-md-4 ${styles.rightColumn}`}>
                  <address className={`${styles.addressBlock} ${styles.footerContactInfo}`}>
                    <p>
                      <strong>Phone:</strong>
                      <br />
                      <a href="tel:01614072791" className={styles.inlineLinkPlain}>
                        0161-4072791
                      </a>
                    </p>
                    <p>
                      <strong>Email:</strong>
                      <br />
                      <a href="mailto:wecare@ourbroking.com" className={styles.inlineLinkPlain}>
                        wecare@ourbroking.com
                      </a>
                    </p>
                  </address>
                </div>
              </div>
            </div>
          </div>

          <div className="col-lg-6 col-md-12">
            <div className="row">
              {footerGroups.map((group) => (
                <div key={group.id} className={`col-lg-3 col-sm-6 ${styles.rightColumn}`}>
                  <nav aria-labelledby={`footer-${group.id}-heading`} className={styles.footerNav}>
                    <p id={`footer-${group.id}-heading`} className={styles.navTitle}>
                      {group.title}
                    </p>
                    <ul className={styles.footerList}>
                      {group.links.map((link) => (
                        <li key={`${group.id}-${link.label}`}>{renderFooterLink(link)}</li>
                      ))}
                    </ul>
                  </nav>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      <div className={`container ${styles.complianceShell}`}>
        <hr className={styles.separator} />

        <div className={styles.complianceStack}>
          <section
            className={styles.complianceCard}
            aria-labelledby="footer-disclaimer-heading"
            data-testid="disclaimer-card"
          >
            <div className={styles.cardHeader}>
              <h2 id="footer-disclaimer-heading" className={styles.sectionHeading}>
                Disclaimer
              </h2>
            </div>

            <div className={styles.cardBody}>
              <p className={styles.leadText}>
                <strong>
                  Investment in securities market is subject to market risks; read all the related documents carefully before investing.
                </strong>
              </p>

              <p className={styles.disclaimerText}>
                <span className={styles.inlineStrong}>OUR BROKING SERVICES LLP:</span> Member of NSE &amp; BSE – SEBI Registration no.: INZ000309758 | CDSL: Depository services - SEBI DP Registration No.: IN-DP-750-2023.
              </p>

              <p className={styles.disclaimerText}>
                Exchange/DP Member Code : NSE Trading Member Code - 90314 | NSE Clearing Member Code - M70057 | BSE Member Code - 6801 | DP ID (CDSL)- 12098900.
              </p>

              <p className={styles.disclaimerText}>
                For any complaints/grievances pertaining to securities broking, please write to{' '}
                <a href="mailto:wecare@ourbroking.com" className={styles.inlineLink}>
                  wecare@ourbroking.com
                </a>
                , for DP related queries write to{' '}
                <a href="mailto:dp@ourbroking.com" className={styles.inlineLink}>
                  dp@ourbroking.com
                </a>
                .
              </p>

              <p className={styles.disclaimerText}>
                Please ensure you carefully read the{' '}
                <a
                  href="/pdf/RISK_DISCLOSURE_DOCUMENT.pdf"
                  target="_blank"
                  rel="noopener noreferrer"
                  className={styles.inlineLink}
                >
                  Risk Disclosure Document
                  <span className={styles.srOnly}> opens in a new tab</span>
                </a>{' '}
                as prescribed by SEBI.
              </p>
            </div>
          </section>

          <section
            className={styles.complianceCard}
            aria-labelledby="attention-investors-heading"
            data-testid="attention-investors-card"
          >
            <div className={styles.cardHeader}>
              <div className={styles.headerRow}>
                <h2 id="attention-investors-heading" className={styles.sectionHeading}>
                  Attention Investors
                </h2>

                <button
                  type="button"
                  className={`${styles.actionButton} ${styles.secondaryButton} ${styles.pauseDesktopOnly}`}
                  onClick={() => setIsTickerPaused((currentValue) => !currentValue)}
                  aria-controls="attention-investors-ticker"
                  aria-pressed={isTickerPaused}
                  data-testid="ticker-toggle"
                  aria-label={
                    isTickerPaused
                      ? 'Resume scrolling investor awareness messages'
                      : 'Pause scrolling investor awareness messages'
                  }
                >
                  {isTickerPaused ? 'Resume ticker' : 'Pause ticker'}
                </button>
              </div>
            </div>

            <div className={styles.cardBody}>
              <div
                id="attention-investors-ticker"
                className={`${styles.ticker} ${isTickerPaused ? styles.tickerPaused : ''}`}
                role="region"
                aria-label="Important investor information ticker"
                aria-live="off"
                data-testid="investor-ticker"
              >
                <div className={styles.tickerTrack} data-testid="investor-ticker-track">
                  {[0, 1].map((copyIndex) => (
                    <div
                      key={copyIndex}
                      className={styles.tickerGroup}
                      aria-hidden={copyIndex === 1 ? 'true' : undefined}
                    >
                      {tickerItems.map((item, index) => (
                        <span key={`${copyIndex}-${index}`} className={styles.tickerItem}>
                          <strong>{item.title}</strong>
                          <span>{item.text}</span>
                        </span>
                      ))}
                    </div>
                  ))}
                </div>
              </div>

              <div className={styles.mobileTickerControls}>
                <button
                  type="button"
                  className={`${styles.actionButton} ${styles.secondaryButton}`}
                  onClick={() => setIsTickerPaused((currentValue) => !currentValue)}
                  aria-controls="attention-investors-ticker"
                  aria-pressed={isTickerPaused}
                >
                  {isTickerPaused ? 'Resume ticker' : 'Pause ticker'}
                </button>
              </div>

              <div className={styles.investorNote} role="region" aria-label="Important investor information note">
                Investors should be cautious on unsolicited emails and SMS advising to buy, sell or hold securities and trade only on the basis of informed decision. Investors are advised to invest after conducting appropriate analysis of respective companies and not to blindly follow unfounded rumours, tips etc. Further, investors are encouraged to report any knowledge or evidence of systemic wrongdoing, potential fraud, or unethical behaviour through the anonymous reporting facilities available on BSE &amp; NSE website.
              </div>

              <div className={styles.guidelinesPanel}>
                <h3 className={styles.subsectionTitle}>Awareness regarding guidelines on Margin collection</h3>

                <ol
                  id="attention-investors-list"
                  className={styles.guidelinesList}
                  hidden={!isExpanded}
                  data-testid="margin-guidelines-list"
                >
                  <li>
                    Stock Brokers can accept securities as margin from clients only by way of pledge in the depository system w.e.f. September 1, 2020.
                  </li>
                  <li>
                    Pay minimum 20% upfront margin of the transaction value to trade in the cash market segment.
                  </li>
                  <li>
                    Investors may please refer to the Exchange&apos;s Frequently Asked Questions (FAQs) issued vide circular reference NSE/INSP/45191 dated July 31, 2020, and NSE/INSP/45534 dated August 31, 2020, and other guidelines issued from time to time in this regard.
                  </li>
                  <li>
                    Check your Securities /MF/ Bonds in the consolidated account statement issued by NSDL/CDSL every month.
                  </li>
                  <li>
                    Filing Complaints on{' '}
                    <a
                      href="https://scores.sebi.gov.in/"
                      className={styles.inlineLink}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      SCOREs 2.0
                      <span className={styles.srOnly}> opens in a new tab</span>
                    </a>{' '}
                    - Easy &amp; quick: a. Register on SCORES portal b. Mandatory details for filing complaints on SCORES: i. Name, PAN, Address, Mobile Number, Email ID c. Benefits: i. Effective communication ii. Speedy redressal of the grievances.
                  </li>
                </ol>

                <div className={styles.actionsRow}>
                  <button
                    type="button"
                    className={`${styles.actionButton} ${styles.primaryButton}`}
                    onClick={() => setIsExpanded((currentValue) => !currentValue)}
                    aria-expanded={isExpanded}
                    aria-controls="attention-investors-list"
                    data-testid="margin-guidelines-toggle"
                  >
                    {isExpanded ? 'Collapse margin guidelines' : 'Expand margin guidelines'}
                  </button>
                </div>
              </div>
            </div>
          </section>
        </div>
      </div>

      <div className={`container ${styles.copyright}`}>
        Copyright © 2023 - 2026 Our Broking. All rights reserved.
      </div>
    </footer>
  );
}

export default Footer;